import type { BehaviorNode, Port } from "../blueprint/types.js";
import type {
  BehaviorGraph,
  LayoutNode,
  SceneAnimationEntry,
  SceneBehaviorCatalog,
  SceneCustomEventEntry,
  SceneFunctionEntry,
  SceneVariableEntry,
} from "./types.js";
import { EMPTY_SCENE_CATALOG } from "./types.js";
import {
  isSceneCatalogEntryNameTaken,
  type SceneCatalogNameSection,
} from "./scene-catalog-names.js";
import { DEFAULT_SCENE_ANIMATION_NAMES } from "../blueprint/runtime/scene-animations.js";

export type ClipboardSceneCatalog = SceneBehaviorCatalog;

export type CatalogNameConflict = {
  section: SceneCatalogNameSection;
  entryId: string;
  oldName: string;
};

type CatalogRefSets = {
  animationNames: Set<string>;
  customEventIds: Set<string>;
  functionIds: Set<string>;
  variableIds: Set<string>;
  objectTypeIds: Set<string>;
};

function getPortValue(node: BehaviorNode, portId: string): unknown {
  const ports = (node as { ports?: Port[] }).ports;
  return ports?.find((port) => port.id === portId)?.value;
}

function createEmptyRefs(): CatalogRefSets {
  return {
    animationNames: new Set(),
    customEventIds: new Set(),
    functionIds: new Set(),
    variableIds: new Set(),
    objectTypeIds: new Set(),
  };
}

/**
 * Collects catalog references from a single behavior graph.
 */
export function collectCatalogRefsFromBehaviorGraph(
  graph: BehaviorGraph | undefined,
  refs: CatalogRefSets = createEmptyRefs(),
): CatalogRefSets {
  for (const node of graph?.nodes ?? []) {
    if (node.type === "operator" && node.objectTypeId)
      refs.objectTypeIds.add(node.objectTypeId);
    if ("objectSource" in node && node.objectSource?.startsWith("@object:"))
      refs.objectTypeIds.add(node.objectSource.slice(8));
    for (const port of node.ports ?? [])
      if (port.objectTypeId) refs.objectTypeIds.add(port.objectTypeId);
    if (node.type === "action" && node.kind === "animPlay") {
      const name = String(getPortValue(node, "name") ?? "").trim();
      if (name) refs.animationNames.add(name);
    }
    if (
      node.type === "event" &&
      node.kind === "onCustomSceneEvent" &&
      typeof node.customEventId === "string" &&
      node.customEventId
    ) {
      refs.customEventIds.add(node.customEventId);
    }
    if (
      node.type === "action" &&
      node.kind === "call" &&
      typeof node.functionId === "string" &&
      node.functionId
    ) {
      refs.functionIds.add(node.functionId);
    }
    if (
      node.type === "variable" &&
      node.valueType === "variableRef" &&
      typeof node.variableId === "string" &&
      node.variableId
    ) {
      refs.variableIds.add(node.variableId);
    }
    if (
      node.type === "action" &&
      node.kind === "setVariable" &&
      typeof node.variableId === "string" &&
      node.variableId
    ) {
      refs.variableIds.add(node.variableId);
    }
  }
  return refs;
}

function walkLayoutNode(
  node: LayoutNode,
  visit: (node: LayoutNode) => void,
): void {
  visit(node);
  if (node.kind === "container") {
    for (const child of node.children) {
      walkLayoutNode(child, visit);
    }
  }
  if (node.kind === "component" && node.component === "layoutList") {
    const props = (node.props ?? {}) as {
      rowTemplate?: LayoutNode;
      rowVariants?: Array<{ template?: LayoutNode }>;
    };
    if (props.rowTemplate) {
      walkLayoutNode(props.rowTemplate, visit);
    }
    for (const variant of props.rowVariants ?? []) {
      if (variant.template) {
        walkLayoutNode(variant.template, visit);
      }
    }
  }
}

/**
 * Walks layout nodes (including containers and layoutList templates) and
 * collects catalog references from each node's behavior graph.
 */
export function collectCatalogRefsFromLayoutNodes(
  nodes: LayoutNode[],
): CatalogRefSets {
  const refs = createEmptyRefs();
  for (const root of nodes) {
    walkLayoutNode(root, (node) => {
      collectCatalogRefsFromBehaviorGraph(node.behavior, refs);
    });
  }
  return refs;
}

function findAnimationByName(
  catalog: SceneBehaviorCatalog,
  name: string,
): SceneAnimationEntry | undefined {
  const key = name.trim().toLocaleLowerCase();
  return catalog.animations.find(
    (entry) => entry.name.trim().toLocaleLowerCase() === key,
  );
}

function isDefaultAnimationName(name: string): boolean {
  const key = name.trim().toLocaleLowerCase();
  return DEFAULT_SCENE_ANIMATION_NAMES.some(
    (preset) => preset.toLocaleLowerCase() === key,
  );
}

/**
 * Builds the closed set of catalog entries referenced by the given layout nodes,
 * including transitive dependencies inside event/function/variable/animation graphs.
 * Preset animations (no catalog entry) are omitted.
 */
export function collectReferencedSceneCatalog(
  nodes: LayoutNode[],
  catalog: SceneBehaviorCatalog | undefined,
): SceneBehaviorCatalog {
  if (!catalog) return { ...EMPTY_SCENE_CATALOG };

  const pending = collectCatalogRefsFromLayoutNodes(nodes);
  const collected: SceneBehaviorCatalog = {
    animations: [],
    customEvents: [],
    functions: [],
    variables: [],
    objectTypes: [],
  };
  const seenAnimationIds = new Set<string>();
  const seenEventIds = new Set<string>();
  const seenFunctionIds = new Set<string>();
  const seenVariableIds = new Set<string>();
  const seenObjectTypeIds = new Set<string>();

  const enqueueGraph = (graph: BehaviorGraph | undefined) => {
    collectCatalogRefsFromBehaviorGraph(graph, pending);
  };

  let guard = 0;
  while (guard < 10_000) {
    guard += 1;
    let progressed = false;

    for (const name of [...pending.animationNames]) {
      pending.animationNames.delete(name);
      if (isDefaultAnimationName(name)) continue;
      const entry = findAnimationByName(catalog, name);
      if (!entry || seenAnimationIds.has(entry.id)) continue;
      seenAnimationIds.add(entry.id);
      collected.animations.push(structuredClone(entry));
      enqueueGraph(entry.graph);
      progressed = true;
    }

    for (const id of [...pending.customEventIds]) {
      pending.customEventIds.delete(id);
      if (seenEventIds.has(id)) continue;
      const entry = catalog.customEvents.find((item) => item.id === id);
      if (!entry) continue;
      seenEventIds.add(id);
      collected.customEvents.push(structuredClone(entry));
      enqueueGraph(entry.triggerGraph);
      progressed = true;
    }

    for (const id of [...pending.functionIds]) {
      pending.functionIds.delete(id);
      if (seenFunctionIds.has(id)) continue;
      const entry = catalog.functions.find((item) => item.id === id);
      if (!entry) continue;
      seenFunctionIds.add(id);
      collected.functions.push(structuredClone(entry));
      enqueueGraph(entry.graph);
      progressed = true;
    }

    for (const id of [...pending.variableIds]) {
      pending.variableIds.delete(id);
      if (seenVariableIds.has(id)) continue;
      const entry = catalog.variables.find((item) => item.id === id);
      if (!entry) continue;
      seenVariableIds.add(id);
      collected.variables.push(structuredClone(entry));
      if (entry.outputObjectTypeId)
        pending.objectTypeIds.add(entry.outputObjectTypeId);
      for (const input of entry.inputs)
        if (input.objectTypeId) pending.objectTypeIds.add(input.objectTypeId);
      enqueueGraph(entry.graph);
      progressed = true;
    }

    for (const id of [...pending.objectTypeIds]) {
      pending.objectTypeIds.delete(id);
      if (seenObjectTypeIds.has(id)) continue;
      const entry = catalog.objectTypes?.find((type) => type.id === id);
      if (!entry) continue;
      seenObjectTypeIds.add(id);
      collected.objectTypes!.push(structuredClone(entry));
      if (entry.baseType) pending.objectTypeIds.add(entry.baseType);
      for (const extension of entry.extensions ?? [])
        pending.objectTypeIds.add(extension.typeId);
      enqueueGraph(entry.graph);
      for (const field of entry.fields)
        if (field.objectTypeId) pending.objectTypeIds.add(field.objectTypeId);
      progressed = true;
    }
    if (
      !progressed &&
      pending.animationNames.size === 0 &&
      pending.customEventIds.size === 0 &&
      pending.functionIds.size === 0 &&
      pending.variableIds.size === 0 &&
      pending.objectTypeIds.size === 0
    ) {
      break;
    }
  }

  return collected;
}

export function sceneCatalogHasEntries(
  catalog: SceneBehaviorCatalog | undefined,
): boolean {
  if (!catalog) return false;
  return (
    catalog.animations.length > 0 ||
    catalog.customEvents.length > 0 ||
    catalog.functions.length > 0 ||
    catalog.variables.length > 0 ||
    (catalog.objectTypes?.length ?? 0) > 0
  );
}

/**
 * Detects name conflicts when merging a clipboard catalog into a target catalog.
 */
export function detectSceneCatalogNameConflicts(
  incoming: SceneBehaviorCatalog,
  target: SceneBehaviorCatalog | undefined,
): CatalogNameConflict[] {
  const conflicts: CatalogNameConflict[] = [];

  for (const entry of incoming.animations) {
    if (isSceneCatalogEntryNameTaken("animations", entry.name, target)) {
      conflicts.push({
        section: "animations",
        entryId: entry.id,
        oldName: entry.name,
      });
    }
  }
  for (const entry of incoming.customEvents) {
    if (isSceneCatalogEntryNameTaken("events", entry.name, target)) {
      conflicts.push({
        section: "events",
        entryId: entry.id,
        oldName: entry.name,
      });
    }
  }
  for (const entry of incoming.functions) {
    if (isSceneCatalogEntryNameTaken("functions", entry.name, target)) {
      conflicts.push({
        section: "functions",
        entryId: entry.id,
        oldName: entry.name,
      });
    }
  }
  for (const entry of incoming.variables) {
    if (isSceneCatalogEntryNameTaken("variables", entry.name, target)) {
      conflicts.push({
        section: "variables",
        entryId: entry.id,
        oldName: entry.name,
      });
    }
  }

  for (const entry of incoming.objectTypes ?? [])
    if (isSceneCatalogEntryNameTaken("objects", entry.name, target))
      conflicts.push({
        section: "objects",
        entryId: entry.id,
        oldName: entry.name,
      });
  return conflicts;
}

/**
 * Resolves the final display name for an incoming catalog entry after paste renames.
 */
export function resolveCatalogPasteEntryName(
  entryId: string,
  fallbackName: string,
  renamedNamesByEntryId: Record<string, string>,
): string {
  const renamed = renamedNamesByEntryId[entryId]?.trim();
  return renamed && renamed.length > 0 ? renamed : fallbackName.trim();
}

/**
 * Validates rename proposals for a catalog paste: non-empty names, uniqueness
 * within each section of the incoming set, and no collision with the target catalog.
 *
 * @returns An error code (`empty` | `taken`) or `null` when valid.
 */
export function validateCatalogPasteRenames(
  incoming: SceneBehaviorCatalog,
  target: SceneBehaviorCatalog | undefined,
  renamedNamesByEntryId: Record<string, string>,
): "empty" | "taken" | null {
  const sections: Array<{
    section: SceneCatalogNameSection;
    entries: Array<{ id: string; name: string }>;
  }> = [
    { section: "animations", entries: incoming.animations },
    { section: "events", entries: incoming.customEvents },
    { section: "functions", entries: incoming.functions },
    { section: "variables", entries: incoming.variables },
    { section: "objects", entries: incoming.objectTypes ?? [] },
  ];

  for (const { section, entries } of sections) {
    const seen = new Set<string>();
    for (const entry of entries) {
      const finalName = resolveCatalogPasteEntryName(
        entry.id,
        entry.name,
        renamedNamesByEntryId,
      );
      if (!finalName) return "empty";
      const key = finalName.toLocaleLowerCase();
      if (seen.has(key)) return "taken";
      seen.add(key);
      if (isSceneCatalogEntryNameTaken(section, finalName, target)) {
        return "taken";
      }
    }
  }
  return null;
}

export type CatalogPasteRemap = {
  animationIdByOldId: Map<string, string>;
  animationNameByOldName: Map<string, string>;
  eventIdByOldId: Map<string, string>;
  functionIdByOldId: Map<string, string>;
  variableIdByOldId: Map<string, string>;
  objectTypeIdByOldId?: Map<string, string>;
};

function remapBehaviorGraph(
  graph: BehaviorGraph | undefined,
  remap: CatalogPasteRemap,
): BehaviorGraph | undefined {
  if (!graph) return graph;
  return {
    ...graph,
    nodes: graph.nodes.map((node) => remapBehaviorNode(node, remap)),
  };
}

function remapBehaviorNode(
  node: BehaviorNode,
  remap: CatalogPasteRemap,
): BehaviorNode {
  let next: BehaviorNode = node;
  const mapType = (id: string) => remap.objectTypeIdByOldId?.get(id) ?? id;
  next = {
    ...next,
    ports: next.ports.map((p) =>
      p.objectTypeId ? { ...p, objectTypeId: mapType(p.objectTypeId) } : p,
    ),
  };
  if ("objectSource" in node && node.objectSource?.startsWith("@objectBase:")) {
    const [id, port] = node.objectSource.slice(12).split("/");
    next = {
      ...next,
      objectSource: "@objectBase:" + mapType(id) + "/" + port,
    } as BehaviorNode;
  }
  if (node.type === "operator")
    next = {
      ...next,
      ...(node.objectTypeId
        ? { objectTypeId: mapType(node.objectTypeId) }
        : {}),
      ...(node.objectSource?.startsWith("@object:")
        ? { objectSource: "@object:" + mapType(node.objectSource.slice(8)) }
        : {}),
    } as BehaviorNode;

  if (node.type === "action" && node.kind === "animPlay") {
    const ports = (node.ports ?? []).map((port) => {
      if (port.id !== "name") return port;
      const oldName = String(port.value ?? "").trim();
      const newName = remap.animationNameByOldName.get(oldName) ?? oldName;
      return newName === oldName ? port : { ...port, value: newName };
    });
    next = { ...next, ports } as BehaviorNode;
  }

  if (
    node.type === "event" &&
    node.kind === "onCustomSceneEvent" &&
    node.customEventId
  ) {
    const mapped = remap.eventIdByOldId.get(node.customEventId);
    if (mapped && mapped !== node.customEventId) {
      next = { ...next, customEventId: mapped } as BehaviorNode;
    }
  }

  if (node.type === "action" && node.kind === "call" && node.functionId) {
    const mapped = remap.functionIdByOldId.get(node.functionId);
    if (mapped && mapped !== node.functionId) {
      next = { ...next, functionId: mapped } as BehaviorNode;
    }
  }

  if (
    node.type === "variable" &&
    node.valueType === "variableRef" &&
    node.variableId
  ) {
    const mapped = remap.variableIdByOldId.get(node.variableId);
    if (mapped && mapped !== node.variableId) {
      next = { ...next, variableId: mapped } as BehaviorNode;
    }
  }

  if (
    node.type === "action" &&
    node.kind === "setVariable" &&
    node.variableId
  ) {
    const mapped = remap.variableIdByOldId.get(node.variableId);
    if (mapped && mapped !== node.variableId) {
      next = { ...next, variableId: mapped } as BehaviorNode;
    }
  }

  return next;
}

function remapLayoutNodeBehavior(
  node: LayoutNode,
  remap: CatalogPasteRemap,
): LayoutNode {
  let next = node;
  if (node.behavior) {
    next = {
      ...next,
      behavior: remapBehaviorGraph(node.behavior, remap),
    } as LayoutNode;
  }
  if (next.kind === "container") {
    next = {
      ...next,
      children: next.children.map((child) =>
        remapLayoutNodeBehavior(child, remap),
      ),
    };
  }
  if (next.kind === "component" && next.component === "layoutList") {
    const props = { ...(next.props ?? {}) } as {
      rowTemplate?: LayoutNode;
      rowVariants?: Array<{ name: string; template: LayoutNode }>;
      [key: string]: unknown;
    };
    if (props.rowTemplate) {
      props.rowTemplate = remapLayoutNodeBehavior(props.rowTemplate, remap);
    }
    if (props.rowVariants) {
      props.rowVariants = props.rowVariants.map((variant) => ({
        ...variant,
        template: remapLayoutNodeBehavior(variant.template, remap),
      }));
    }
    next = { ...next, props };
  }
  return next;
}

function newCatalogId(): string {
  return crypto.randomUUID();
}

/**
 * Remaps incoming catalog entries (fresh ids + optional renamed names) and
 * returns remapped layout nodes + catalog entries ready to merge.
 */
export function prepareSceneCatalogPaste(options: {
  nodes: LayoutNode[];
  incomingCatalog: SceneBehaviorCatalog;
  targetCatalog: SceneBehaviorCatalog | undefined;
  /** Map of clipboard entry id → new name chosen by the user for conflicts. */
  renamedNamesByEntryId: Record<string, string>;
}): {
  nodes: LayoutNode[];
  catalogToMerge: SceneBehaviorCatalog;
  remap: CatalogPasteRemap;
} {
  const { nodes, incomingCatalog, renamedNamesByEntryId } = options;
  const remap: CatalogPasteRemap = {
    animationIdByOldId: new Map(),
    animationNameByOldName: new Map(),
    eventIdByOldId: new Map(),
    functionIdByOldId: new Map(),
    variableIdByOldId: new Map(),
    objectTypeIdByOldId: new Map(),
  };

  const animations: SceneAnimationEntry[] = incomingCatalog.animations.map(
    (entry) => {
      const newId = newCatalogId();
      remap.animationIdByOldId.set(entry.id, newId);
      const newName =
        renamedNamesByEntryId[entry.id]?.trim() || entry.name.trim();
      remap.animationNameByOldName.set(entry.name, newName);
      // Also map trimmed variants for animPlay port matching.
      remap.animationNameByOldName.set(entry.name.trim(), newName);
      return {
        ...structuredClone(entry),
        id: newId,
        name: newName,
      };
    },
  );

  const customEvents: SceneCustomEventEntry[] =
    incomingCatalog.customEvents.map((entry) => {
      const newId = newCatalogId();
      remap.eventIdByOldId.set(entry.id, newId);
      const newName =
        renamedNamesByEntryId[entry.id]?.trim() || entry.name.trim();
      return {
        ...structuredClone(entry),
        id: newId,
        name: newName,
      };
    });

  const functions: SceneFunctionEntry[] = incomingCatalog.functions.map(
    (entry) => {
      const newId = newCatalogId();
      remap.functionIdByOldId.set(entry.id, newId);
      const newName =
        renamedNamesByEntryId[entry.id]?.trim() || entry.name.trim();
      return {
        ...structuredClone(entry),
        id: newId,
        name: newName,
      };
    },
  );

  const variables: SceneVariableEntry[] = incomingCatalog.variables.map(
    (entry) => {
      const newId = newCatalogId();
      remap.variableIdByOldId.set(entry.id, newId);
      const newName =
        renamedNamesByEntryId[entry.id]?.trim() || entry.name.trim();
      return {
        ...structuredClone(entry),
        id: newId,
        name: newName,
      };
    },
  );

  const mapType = (id: string | undefined | null) =>
    id ? (remap.objectTypeIdByOldId?.get(id) ?? id) : id;
  for (const entry of incomingCatalog.objectTypes ?? [])
    remap.objectTypeIdByOldId!.set(entry.id, newCatalogId());
  const objectTypes = (incomingCatalog.objectTypes ?? []).map((entry) => ({
    ...structuredClone(entry),
    id: mapType(entry.id)!,
    name: renamedNamesByEntryId[entry.id]?.trim() || entry.name,
    baseType: mapType(entry.baseType) ?? null,
    ...(entry.extensions
      ? {
          extensions: entry.extensions.map((e) => ({
            ...e,
            typeId: mapType(e.typeId)!,
          })),
        }
      : {}),
    ...(entry.graph ? { graph: remapBehaviorGraph(entry.graph, remap) } : {}),
    fields: entry.fields.map((field) => ({
      ...field,
      ...(field.objectTypeId
        ? { objectTypeId: mapType(field.objectTypeId)! }
        : {}),
    })),
  }));
  const remappedCatalog: SceneBehaviorCatalog = {
    ...(objectTypes.length ? { objectTypes } : {}),
    animations: animations.map((entry) => ({
      ...entry,
      graph: remapBehaviorGraph(entry.graph, remap) ?? entry.graph,
    })),
    customEvents: customEvents.map((entry) => ({
      ...entry,
      triggerGraph:
        remapBehaviorGraph(entry.triggerGraph, remap) ?? entry.triggerGraph,
    })),
    functions: functions.map((entry) => ({
      ...entry,
      graph: remapBehaviorGraph(entry.graph, remap) ?? entry.graph,
    })),
    variables: variables.map((entry) => ({
      ...entry,
      ...(entry.outputObjectTypeId
        ? { outputObjectTypeId: mapType(entry.outputObjectTypeId)! }
        : {}),
      inputs: entry.inputs.map((input) => ({
        ...input,
        ...(input.objectTypeId
          ? { objectTypeId: mapType(input.objectTypeId)! }
          : {}),
      })),
      graph: remapBehaviorGraph(entry.graph, remap) ?? entry.graph,
    })),
  };

  return {
    nodes: nodes.map((node) => remapLayoutNodeBehavior(node, remap)),
    catalogToMerge: remappedCatalog,
    remap,
  };
}

/**
 * Merges catalog entries into a target catalog (append).
 */
export function mergeSceneCatalogs(
  target: SceneBehaviorCatalog | undefined,
  incoming: SceneBehaviorCatalog,
): SceneBehaviorCatalog {
  const base = target ?? EMPTY_SCENE_CATALOG;
  return {
    animations: [...base.animations, ...incoming.animations],
    customEvents: [...base.customEvents, ...incoming.customEvents],
    functions: [...base.functions, ...incoming.functions],
    variables: [...base.variables, ...incoming.variables],
    ...((base.objectTypes?.length ?? 0) + (incoming.objectTypes?.length ?? 0)
      ? {
          objectTypes: [
            ...(base.objectTypes ?? []),
            ...(incoming.objectTypes ?? []),
          ],
        }
      : {}),
  };
}
